import { HelpersService } from './../helpers/helpers.service';
import {Device} from "@capacitor/device";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {Platform} from "@ionic/angular";
import {Observable, Observer} from "rxjs";
import {map} from "rxjs/operators";
import {
  BASE_REST_HEADER,
  FCM_DEVICES_ENDPOINT,
  WENEA_USER_COUNTRIES,
  WENEA_USER_GROUPS,
  WENEA_USER_LOGIN,
  WENEA_USER_PASSWORD_CHANGE,
  WENEA_USER_PASSWORD_RESET,
  WENEA_USER_PAYMENT_TOKEN,
  WENEA_USER_PROFILE,
  WENEA_USER_REGISTER,
  USER_ENDPOINT,
  WENEA_VERSION_ENDPOINT,
  WENEA_ANDROID_STORE_LINK,
  WENEA_IPHONE_STORE_LINK,
  WENEA_VERSION,
  EVCHARGE_TRANSACTION_DEV,
  WENEA_USER_LOGOUT,
  WENEA_USER_SOCIAL_LOGIN,
  WENEA_APP_CONFIG,
} from "src/app/utils/properties";
import {EventsService} from "../events/events.service";
import {Capacitor} from "@capacitor/core";
import {TranslateService} from "@ngx-translate/core";
import {App} from "@capacitor/app";
import {Browser} from "@capacitor/browser";


/**
 *  @description contains all user-data related operations and data, including:
 *  login/logout, register, authentication functions, user info & edit
 */
@Injectable({
  providedIn: "root",
})
export class AuthService {
  private user: any;
  private userToken: any;
  private userMail: any;
  private userGroup: any;
  private groupNames: Array<any>;
  private userIdTag: any;
  private userFCMToken: any;
  private vehicles: any;
  public preferences: any;
  public localtoken;
  public isAnon: boolean;
  public isAnonAllowed: boolean;

  constructor(
    private http: HttpClient,
    private events: EventsService,
    private platform: Platform,
    private translate: TranslateService,
  ) {
    this.isAnon = false;
    this.isAnonAllowed = false;
    this.preferences = {
      filter: {
        connectorTypesFilter: [0, 1, 4],
        powerFilter: 0,
        includeExternal: false,
        onlyUsableFilter: true,
        powerAliasesFilter: ['AC', 'DC', 'HPC']
      }
    };
  }

  /**
   * Login #1 - Standard login with user & password
   *
   * @param user user email
   * @param pass user password
   *
   * @returns operation result
   */
  public login(user: string, pass: string): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.http
        .post(
          WENEA_USER_LOGIN,
          {
            email: user,
            password: pass,
          },
          {headers: BASE_REST_HEADER}
        )
        .subscribe(
          (res: any) => {
            this.userToken = res.token;
            this.userMail = user;
            this.isAnon = false;
            if (this.userFCMToken){
              this.sendFCMToken().subscribe(
                (res) => {
                  console.log("send after login ok");
                },
                (err) => {
                  console.log("send after login ko");
                  this.events.publish("closeStationCard", "close station card");
                }
              );
            }
            // Publish event "logged in"
            this.events.publish("login", "user logged");
            observer.next(res);
            observer.complete();
          },
          (err) => {
            observer.error(err);
          }
        );
    });
  }

  /**
   * Login #2 - Store user token
   *
   * @param token - token present
   *
   * @returns observer with token
   */
  public loginToken(token: string): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.userToken = token;
      this.getUserInfo().subscribe(
        (res) => {
          this.isAnon = false;
          if (this.userFCMToken){
            this.sendFCMToken().subscribe(
              (res) => {
                console.log("send after login ok");
              },
              (err) => {
                console.log("send after login ko");
                this.events.publish("closeStationCard", "close station card");
              }
            );
          }

          res = {result: true};
          observer.next(res);
          observer.complete();
        },
        (err) => {
          if (err.status != 400){ // invalid token
            this.userToken = null;
            observer.error(err);
          }
          else{
            let res = {result: true}; // token is valid but user has no profile
            observer.next(res);
            observer.complete();
          }
        }
      );
    });
  }

  private clearUser(){
    localStorage.removeItem("token");
    this.user = undefined;
    this.userMail = undefined;
    this.userToken = undefined;
    this.localtoken = undefined;
    this.isAnon = false;
    this.events.publish("closeStationCard", "close station card");
    this.events.publish("logout", "user logged out");
  }

  public logout(): Observable<any> {
    if (this.userFCMToken){
      this.deleteFCMToken().subscribe(
        (res) => {
          console.log("fcm token deleted OK ", res);
        },
        (err) => {
          console.log("error deleting fcm token ", err);
        }
      );
    }
    return new Observable((observer: Observer<{}>) => {
      this.http
        .post(
          WENEA_USER_LOGOUT,
          null,
          {headers: this.buildSelfTokenHeader()}
        )
        .subscribe(
          (res) => {
            this.clearUser();
            observer.next("logged out");
            observer.complete();
          },
          (err) => {
            observer.error(err);
          }
        );
    });
  }
  public register(username: string, password: string): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.http
        .post(
          WENEA_USER_REGISTER,
          {email: username, password1: password, password2: password},
          {headers: BASE_REST_HEADER}
        )
        .subscribe(
          (res) => {
            console.log("usuario registrado ", username);
            let newUser: any = res;
            this.userToken = newUser.token;
            this.userMail = username;
            this.isAnon = false;
            observer.next(res);
            observer.complete();
          },
          (err) => {
            observer.error(err);
          }
        );
    });
  }

  public passwordReset(username: string): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.http
        .post(
          WENEA_USER_PASSWORD_RESET,
          {email: username},
          {headers: BASE_REST_HEADER}
        )
        .subscribe(
          (res) => {
            observer.next(res);
            observer.complete();
          },
          (err) => {
            observer.error(err);
          }
        );
    });
  }

  public passwordChange(
    newPassword: string,
    newPasswordConfirm: string
  ): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.http
        .post(
          WENEA_USER_PASSWORD_CHANGE,
          {new_password1: newPassword, new_password2: newPasswordConfirm},
          {headers: BASE_REST_HEADER}
        )
        .subscribe(
          (res) => {
            observer.next(res);
            observer.complete();
          },
          (err) => {
            observer.error(err);
          }
        );
    });
  }

  public getUserGroupNames(): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.http
        .get(`${WENEA_USER_GROUPS}?limit=60`, {
          headers: this.buildSelfTokenHeader(),
        })
        .subscribe(
          (res: any) => {
            this.groupNames = res.results;
            observer.next(res.results);
            observer.complete();
          },
          (err) => {
            observer.error(err);
          }
        );
    });
  }

  public getCountryNames(): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.http
        .get(`${WENEA_USER_COUNTRIES}`, {
          headers: this.buildSelfTokenHeader(),
        })
        .subscribe(
          (res) => {
            observer.next(res);
            observer.complete();
          },
          (err) => {
            observer.error(err);
          }
        );
    });
  }

  /**
   * Create new user info
   *
   * @param newUser new user info
   *
   * @returns Observable with request result
   */
  public createUserInfo(newUser: any): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.http
        .post(WENEA_USER_PROFILE, newUser, {
          headers: this.buildSelfTokenHeader(),
        })
        .subscribe(
          (res) => {
            this.user = res;
            observer.next(res);
            observer.complete();
          },
          (err) => {
            observer.error(err);
          }
        );
    });
  }

  /**
   * Request all user info & fill userGroup attribute
   *
   * @returns Observable with request result
   */
  public getUserInfo(): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.http
        .get(WENEA_USER_PROFILE, {headers: this.buildSelfTokenHeader()})
        .subscribe(
          (res: any) => {
            this.isAnon = false;
            this.user = res.user;
            if (!this.user){
              this.events.publish("noPrivateInfo", "");
            }
            else {
              this.userMail = res.user.mail;
              console.log("Hemos obtenido el usuario ", this.user);
              this.userIdTag = res.user.id_tag;
              this.userGroup = [];
              if (this.user.groups) {
                this.userGroup = this.user.groups;
              }
              this.vehicles = res.user.vehicles;
            }
            observer.next(res);
            observer.complete();
          },
          (err: any) => {
            if(err.status == 400){
              this.events.publish("noPrivateInfo", "no private info")
            }
            observer.error(err);
          }
        );
    });
  }

  /**
   * Edit user info
   *
   * @param newUser new user info
   *
   * @returns Observable with request result
   */
  public updateUserInfo(newUser: any): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.http
        .put(WENEA_USER_PROFILE, newUser, {
          headers: this.buildSelfTokenHeader(),
        })
        .subscribe(
          (res) => {
            this.user = res;
            observer.next(res);
            observer.complete();
          },
          (err) => {
            observer.error(err);
          }
        );
    });
  }

  public deleteUser(): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.http
        .delete(USER_ENDPOINT, {headers: this.buildSelfTokenHeader()})
        .subscribe(
          (res) => {
            this.clearUser();
            observer.next(res);
            observer.complete();
          },
          (err) => {
            observer.error(err);
          }
        );
    });
  }

  /**
   * Check if the current user is in a group, given name
   *
   * @param groupName name of the group to check
   */
  public checkUserInGroup(groupName: any): Boolean {
    let result = this.userGroup.find((element) => element.name == groupName);
    return result != undefined;
  }

  /**
   * Check if the current user has valid paymentInfo
   * @returns true if the user has valid paymentInfo
   * */
  public checkUserHasValidPaymentInfo(): Boolean {
    console.log("Checkeando si el usuario tiene paymentInfo...");
    let result = true;
    let paymentInfo = this.user.paymentInfo;
    if (paymentInfo != undefined) {
      paymentInfo.forEach((element) => {
        if (element.expirationDate != null) {
          let year = Number(element.expirationDate.substring(0, 4));
          let month = Number(element.expirationDate.substring(4, 6))-1;
          let date = new Date(year,month);
          let today = new Date();
          let todayDate = new Date(today.getFullYear(),today.getMonth());
          if (date < todayDate) {
            console.log("CADUCAO ERMANO");
            result = false;
          }
        }
      });
    }

    return result;
  }

  public saveFCMToken(token: string) {
    console.log("saving token ", token);
    this.userFCMToken = token;
    if (token){
      if (this.userToken){
        this.sendFCMToken().subscribe(
          (res) => {
            console.log("send after login ok");
          },
          (err) => {
            console.log("send after login ko");
            this.events.publish("closeStationCard", "close station card");
          }
        );
      }
    }
    console.log("EL TOKEN", this.userFCMToken);
  }

  public getFCMToken() {
    return this.userFCMToken;
  }

  /**
   * Send FCM token to backend
   *
   * @returns Observable with operation result
   */
  public sendFCMToken(): Observable<any> {
    let type;
    if (this.platform.is("android")) {
      type = "android";
    }
    if (this.platform.is("ios")) {
      type = "ios";
    }
    return new Observable((observer: Observer<{}>) => {
      this.http
        .post(
          FCM_DEVICES_ENDPOINT,
          {registration_id: this.userFCMToken, type: type},
          {headers: this.buildSelfTokenHeader()}
        )
        .subscribe(
          (res) => {
            observer.next(res);
            observer.complete();
          },
          (err) => {
            console.log("EL FALLO EN EL REQUEST", this.userFCMToken);
            observer.error(err);
          }
        );
    });
  }

  public deleteFCMToken(): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.http.delete(`${FCM_DEVICES_ENDPOINT}${this.userFCMToken}/`, {headers: this.buildSelfTokenHeader()}).subscribe(
        (res) => {
          observer.next(res);
          observer.complete();
        },
        (err) => {
          observer.error(err);
        }
      );
    });
  }

  public versionCheck() {
    this.http
      .post(
        WENEA_VERSION_ENDPOINT,
        {version: WENEA_VERSION},
        {headers: BASE_REST_HEADER}
      )
      .subscribe(
        (res: any) => {
          console.log("version check OK result ", res.Upgrade);
          if (res.Upgrade == true) {
            let msg = this.translate.instant("toasts.upgrade");
            if (window.confirm(msg)) {
              if (Capacitor.getPlatform() == "android") {
                window.open(WENEA_ANDROID_STORE_LINK, "_system");
              } else if (Capacitor.getPlatform() == "ios") {
                //window.open(WENEA_IPHONE_STORE_LINK, '_system');
                Browser.open({
                  url: WENEA_IPHONE_STORE_LINK,
                  toolbarColor: "#ffffff",
                  presentationStyle: "popover",
                });
              }
              App.exitApp();
            } else {
              App.exitApp();
            }
          }
        },
        (err) => {
          console.log(err);
        }
      );
  }

  /**
   * Build a header with a given token
   *
   * @param token backend token
   *
   * @returns header with token
   */
  public buildTokenHeader(token: string): HttpHeaders {
    let header = new HttpHeaders({
      "Accept-Language": this.translate.getDefaultLang(),
      "Content-Type": "application/json",
      Authorization: "Token " + token,
      "X-App-Version": WENEA_VERSION,
      "X-App-Name": "KIOSK"
    });
    return header;
  }

  /**
   * Check if user is currently logged in
   */
  public isLoggedIn(): boolean {
    return ((this.userToken)?true:false) || this.isAnon;
  }

  /**
   * Build a header with the current user's token
   *
   * @returns header with token or base header if not logged in
   */
  public buildSelfTokenHeader(): HttpHeaders {
    let header: any;
    if (this.userToken) {
      header = this.buildTokenHeader(this.userToken);
    } else {
      header = BASE_REST_HEADER;
    }
    return header;
  }

  public getUserPaymentToken() {
    return this.http
      .post(
        WENEA_USER_PAYMENT_TOKEN,
        {},
        {headers: this.buildSelfTokenHeader()}
      )
      .pipe(map((res: any) => res));
  }

  public sendFeedback(feedback: any, transaction_id: any) {
    let data = {
      "score": feedback,
    };
    return new Observable((observer: Observer<{}>) => {
      this.http
        .post(`${EVCHARGE_TRANSACTION_DEV}/${transaction_id}/feedback/`, data, {
          headers: this.buildSelfTokenHeader(), withCredentials:this.isAnon
        })
        .subscribe(
          (res) => {
            observer.next(res);
            observer.complete();
          },
          (err) => {
            observer.error(err);
          }
        );
    });
  }

  public parseAppConfig(): Observable<any> {
    return new Observable((observer: Observer<{}>) => {
      this.http
        .get(WENEA_APP_CONFIG, {headers: this.buildSelfTokenHeader()})
        .subscribe(
          (res: any) => {
            this.isAnonAllowed = res.feature_flags.anonymous_transactions;
            observer.next(res);
            observer.complete();
          },
          (err: any) => {
            if(err.status == 400){
              this.events.publish("noPrivateInfo", "no private info")
            }
            observer.error(err);
          }
        );
    });
  }

  public getUserMail(): string {
    return this.userMail;
  }

  public setUserMail(mail): void {
    this.userMail = mail;
  }

  public getUserToken(): string {
    return this.userToken;
  }

  public getUser(): any {
    return this.user;
  }

  public setUser(user): any {
    this.user = user;
  }

  public getIdTag(): string {
    return this.userIdTag;
  }
}
